import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAIResult, loadAIResult } from './aiSession';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getStudyScope, isStudyScopeCurrent } from './studyStorage';

export function useSavedAIResult() {
  const { user, isRehydrated } = useAuth();
  const owner = getStudyScope();
  const [params] = useSearchParams();
  const generationId = params.get('generation') || undefined;
  const [state, setState] = useState(() => ({
    owner, data: getAIResult(generationId), loading: Boolean(generationId && !getAIResult(generationId)),
  }));
  useEffect(() => {
    if (!isRehydrated) return;
    let active = true;
    const current = getAIResult(generationId);
    setState({ owner, data: current, loading: Boolean(generationId && !current) });
    if (generationId && !current) {
      void loadAIResult(generationId).then(data => {
        if (active && isStudyScopeCurrent(owner)) setState({ owner, data, loading: false });
      }).catch(() => {
        if (active && isStudyScopeCurrent(owner)) setState({ owner, data: null, loading: false });
      });
    }
    return () => { active = false; };
  }, [generationId, owner, user?.id, isRehydrated]);
  // A changed URL must never display the last route's lesson, even for one frame.
  const data = !isRehydrated || state.owner !== owner ||
    (generationId && state.data?.id !== generationId) ? null : state.data;
  return { data, generationId, loading: state.loading };
}

export function SavedLessonUnavailable({ loading }: { loading: boolean }) {
  const { resolvedTheme } = useTheme();
  return (
    <div className="study-shell min-h-[100dvh] flex items-center justify-center p-6" data-theme={resolvedTheme}>
      <div className="study-card rounded-3xl p-6 text-center max-w-sm">
        <h1 className="text-lg font-semibold">{loading ? 'Opening your lesson…' : 'Saved lesson unavailable'}</h1>
        {!loading && <p className="mt-3 text-sm">This lesson is not saved for this account on this device. Older unowned records cannot safely be restored.</p>}
        <Link className="inline-block mt-5 underline" to="/history">Back to History</Link>
      </div>
    </div>
  );
}
